import * as React from 'react';
import {Button, Cluster, Dialog, Heading, Icon, Table, TdMultilineTruncate, Template, TSize} from '@pluto-tv/assemble';
import {reorderList} from 'helpers/dragAndDrop';
import carouselRoutes from 'routes/programming.routes';
import {IHubCarousel} from 'models/hubConfigs';
import {isEqual} from 'lodash-es';
import {displayModels, tileSizes} from 'components/carouselList/components/helpers/carouselServiceOptions';
import {useHubCarouselItemsCache} from 'views/programming/hubManager/hooks/useHubCarouselItemsCache';
import {getPrefixedUrl} from 'routes';

interface IHubConfigProps {
  hubCarousels: IHubCarousel[];
  onClose: () => void;
  onApplyOrder: (hubCarousels: IHubCarousel[]) => void;
}

const ReorderPanel = ({hubCarousels, onClose, onApplyOrder}: IHubConfigProps): JSX.Element => {
  const [carouselList, setCarouselList] = React.useState<IHubCarousel[]>([]);
  const [selectedRows, setSelectedRows] = React.useState<IHubCarousel[]>([]);

  const {cachedCarouselItems, isError, isLoading} = useHubCarouselItemsCache(hubCarousels);

  React.useEffect(() => {
    if (cachedCarouselItems.length > 0 && !isLoading && carouselList.length === 0) {
      const hubCarouselList = hubCarousels.map(carousel => {
        const carouselConfig = cachedCarouselItems.find(result => result.id === carousel.id);

        return {...carousel, displayModel: carouselConfig?.displayModel};
      });
      setCarouselList(hubCarouselList);
    }
  }, [cachedCarouselItems, carouselList.length, hubCarousels, isLoading]);

  const handleApplyOrder = () => {
    onApplyOrder(carouselList);
    onClose();
  };

  const handleDrop = (_from: string, _to: string, fromIndex: number[], toIndex: number) => {
    setCarouselList(reorderList(fromIndex, toIndex, carouselList));
  };

  const handleSelect = React.useCallback((rows: IHubCarousel[]) => {
    setSelectedRows(rows);
  }, []);

  const handleMoveUp = React.useCallback(
    (index: number) => {
      setCarouselList(reorderList([index], index - 1, carouselList));
    },
    [carouselList],
  );

  const handleMoveDown = React.useCallback(
    (index: number) => {
      setCarouselList(reorderList([index], index + 2, carouselList));
    },
    [carouselList],
  );

  const isApplyOrderDisabled = React.useMemo(() => {
    const modelIds = (hubCarousels || []).map(carousel => carousel.id);
    const carouselListIds = (carouselList || []).map(carousel => carousel.id);

    return isEqual(modelIds, carouselListIds);
  }, [carouselList, hubCarousels]);

  const columns = React.useMemo(
    () => [
      {
        label: 'Order No.',
        colWidth: '6.875rem' as TSize,
        transform: (row: IHubCarousel, _, index: number) => <div>{index + 1}</div>,
      },
      {
        label: 'Carousel Name',
        transform: (row: IHubCarousel) => (
          <TdMultilineTruncate
            row={row}
            text={row.displayName}
            title={row.displayName}
            url={getPrefixedUrl(carouselRoutes.paths.carouselEditDetailsPage.replace(':id', row.id))}
            truncateOnLine={2}
          />
        ),
      },
      {
        label: 'Display Model',
        transform: (row: IHubCarousel) => (
          <TdMultilineTruncate
            row={row}
            text={
              Object.entries(displayModels).find(([, value]) => value === (row.displayModel as any))?.[0] ||
              row.displayModel ||
              ''
            }
            title={row.displayModel}
            truncateOnLine={2}
          />
        ),
      },
      {
        label: 'Tile Size',
        transform: (row: IHubCarousel) => (
          <TdMultilineTruncate
            row={row}
            text={Object.entries(tileSizes).find(([, value]) => value === (row.tileSize as any))?.[0] || row.tileSize}
            title={row.tileSize}
            truncateOnLine={2}
          />
        ),
      },
      {
        label: 'Actions',
        colWidth: '3.125rem' as TSize,
        transform: (row: IHubCarousel, _, index: number) => {
          const upDisabled = index === 0 || selectedRows.length > 1;
          const downDisabled = index === carouselList.length - 1 || selectedRows.length > 1;
          return (
            <Cluster space='xxsmall'>
              <div className={upDisabled ? 'up-disabled' : ''}>
                <Icon disabled={upDisabled} onClick={() => handleMoveUp(index)} icon='directionup' />
              </div>
              <div className={downDisabled ? 'down-disabled' : ''}>
                <Icon disabled={downDisabled} onClick={() => handleMoveDown(index)} icon='directiondown' />
              </div>
            </Cluster>
          );
        },
      },
    ],
    [carouselList.length, handleMoveDown, handleMoveUp, selectedRows.length],
  );

  return (
    <Dialog isOpen={true} onClose={onClose} height='75%' width='850px' id='reorderPanel'>
      <Template label='header'>
        <Heading level='h2'>Carousel List</Heading>
      </Template>
      <Template label='body'>
        <Table<IHubCarousel>
          onDrop={handleDrop}
          draggable={true}
          predicate='id'
          onSelect={handleSelect}
          dragKey='carouselListReorder'
          dropKeys={['carouselListReorder']}
          id='carouselListReorder'
          selectable='multiple'
          wrapContent={true}
          cols={columns}
          rows={carouselList}
          selected={selectedRows}
          emptyMsg={isError}
          loading={isLoading}
        />
      </Template>
      <Template label='footer'>
        <Cluster justify='space-between'>
          <div></div>
          <Cluster space='small'>
            <Button id='cancelBtn' ghost={true} size='small' onClick={onClose}>
              Cancel
            </Button>
            <Button
              id='applyOrderBtn'
              type='primary'
              size='small'
              onClick={handleApplyOrder}
              state={isApplyOrderDisabled ? 'disabled' : ''}
            >
              Apply Order
            </Button>
          </Cluster>
        </Cluster>
      </Template>
    </Dialog>
  );
};

export default ReorderPanel;
